import { Flex, Text, IconButton, Separator, Button } from "@radix-ui/themes";
import { PlusIcon } from "lucide-react";
import Select, { SelectOption } from "@/components/shared/select";
import { Input } from "@/components/ui/input";
import { EscalationRuleOperationType } from "@/v2/entities/escalation-rule/EscalationRule.model";
import { EscalationRuleFormData } from "../../EscalationRuleForm";
import { TicketFieldsClientResponse } from "@/v2/entities/escalation-rule/EscalationRule.api";
import { useCallback, useState, useMemo } from "react";
import { DropdownMenu } from "@/components/shared/dropdown-menu";
import { Enrichment } from "@/v2/entities/enrichment/Enrichment.model";

interface CustomFieldsSectionProps {
  value: EscalationRuleFormData;
  onChange: (updates: Partial<EscalationRuleFormData>) => void;
  onRemove: () => void;
  customFields: Array<{
    fieldId: string;
    fieldName: string;
    value: string;
    valueName: string;
  }>;
  setCustomFields: (
    fields: Array<{
      fieldId: string;
      fieldName: string;
      value: string;
      valueName: string;
    }>
  ) => void;
  ticketFields: TicketFieldsClientResponse[] | undefined;
  loadingTicketFields: boolean;
  enrichments: Enrichment[];
  loadingEnrichments: boolean;
}

export function CustomFieldsSection({
  value,
  onChange,
  onRemove,
  customFields,
  setCustomFields,
  ticketFields,
  loadingTicketFields,
  enrichments,
  loadingEnrichments,
}: CustomFieldsSectionProps) {
  const [selectedCustomField, setSelectedCustomField] = useState<string>("");
  const [selectedCustomFieldValue, setSelectedCustomFieldValue] =
    useState<string>("");
  const [customFieldTextValue, setCustomFieldTextValue] = useState<string>("");

  const customFieldOptions =
    ticketFields?.map((field) => ({
      value: field.id,
      label: field.name,
    })) ?? [];

  const customFieldValueOptions = (() => {
    if (!selectedCustomField || !ticketFields) return [];

    const selectedField = ticketFields.find(
      (field) => field.id === selectedCustomField
    );
    return (
      selectedField?.customFieldOptions?.map((option) => ({
        value: option.id.toString(),
        label: option.name,
      })) ?? []
    );
  })();

  const addCustomField = useCallback(() => {
    if (!selectedCustomField) return;

    const selectedField = ticketFields?.find(
      (field) => field.id === selectedCustomField
    );
    if (!selectedField) return;

    let fieldValue = "";
    let valueName = "";

    if (customFieldValueOptions.length > 0 && selectedCustomFieldValue) {
      const selectedOption = selectedField.customFieldOptions?.find(
        (option) => option.id.toString() === selectedCustomFieldValue
      );
      if (selectedOption) {
        fieldValue = selectedOption.value;
        valueName = selectedOption.name;
      }
    } else if (customFieldTextValue.trim()) {
      // Using custom text
      fieldValue = customFieldTextValue.trim();
      valueName = customFieldTextValue.trim();
    }

    if (!fieldValue) return;

    // Check if this field is already added
    const existingField = customFields.find(
      (cf) => cf.fieldId === selectedCustomField
    );
    if (existingField) return;

    const newCustomField = {
      fieldId: selectedCustomField,
      fieldName: selectedField.name,
      value: fieldValue,
      valueName,
    };

    setCustomFields([...customFields, newCustomField]);

    // Add to escalation rule operations
    const newOperations = [
      ...value.escalationRuleOperations,
      {
        type: EscalationRuleOperationType.ADD_CUSTOM_FIELD,
        enabled: true,
        key: selectedCustomField,
        value: fieldValue,
      },
    ];

    onChange({ escalationRuleOperations: newOperations });

    // Reset form
    setSelectedCustomField("");
    setSelectedCustomFieldValue("");
    setCustomFieldTextValue("");
  }, [
    selectedCustomField,
    ticketFields,
    customFieldValueOptions,
    selectedCustomFieldValue,
    customFieldTextValue,
    customFields,
    value.escalationRuleOperations,
    onChange,
    setCustomFields,
  ]);

  const removeCustomField = useCallback(
    (fieldIdToRemove: string) => {
      setCustomFields(
        customFields.filter((cf) => cf.fieldId !== fieldIdToRemove)
      );

      const filteredOperations = value.escalationRuleOperations.filter(
        (operation) =>
          !(
            operation.type === EscalationRuleOperationType.ADD_CUSTOM_FIELD &&
            operation.key === fieldIdToRemove
          )
      );

      onChange({ escalationRuleOperations: filteredOperations });
    },
    [customFields, value.escalationRuleOperations, onChange, setCustomFields]
  );

  const addQuackTopicTag = useCallback(() => {
    if (!selectedCustomField) return;

    const quackTopicTag = "{Topic}";
    const selectedField = ticketFields?.find(
      (field) => field.id === selectedCustomField
    );

    if (
      selectedField &&
      !customFields.some((cf) => cf.fieldId === selectedCustomField)
    ) {
      const newCustomField = {
        fieldId: selectedCustomField,
        fieldName: selectedField.name,
        value: quackTopicTag,
        valueName: quackTopicTag,
      };

      setCustomFields([...customFields, newCustomField]);

      const newOperations = [
        ...value.escalationRuleOperations,
        {
          type: EscalationRuleOperationType.ADD_CUSTOM_FIELD,
          enabled: true,
          key: selectedCustomField,
          value: quackTopicTag,
        },
      ];

      onChange({ escalationRuleOperations: newOperations });
    }
  }, [
    selectedCustomField,
    ticketFields,
    customFields,
    value.escalationRuleOperations,
    onChange,
    setCustomFields,
  ]);

  const addEnrichmentField = useCallback(
    (enrichmentName: string) => {
      if (!selectedCustomField) return;

      const enrichmentValue = `{${enrichmentName}}`;
      const selectedField = ticketFields?.find(
        (field) => field.id === selectedCustomField
      );

      if (
        selectedField &&
        !customFields.some((cf) => cf.fieldId === selectedCustomField)
      ) {
        const newCustomField = {
          fieldId: selectedCustomField,
          fieldName: selectedField.name,
          value: enrichmentValue,
          valueName: enrichmentValue,
        };

        setCustomFields([...customFields, newCustomField]);

        const newOperations = [
          ...value.escalationRuleOperations,
          {
            type: EscalationRuleOperationType.ADD_CUSTOM_FIELD,
            enabled: true,
            key: selectedCustomField,
            value: enrichmentValue,
          },
        ];

        onChange({ escalationRuleOperations: newOperations });
      }
    },
    [
      selectedCustomField,
      ticketFields,
      customFields,
      value.escalationRuleOperations,
      onChange,
      setCustomFields,
    ]
  );

  const addIsAuthenticatedField = useCallback(() => {
    if (!selectedCustomField) return;

    const isAuthenticatedValue = "{is_authenticated}";
    const selectedField = ticketFields?.find(
      (field) => field.id === selectedCustomField
    );

    if (
      selectedField &&
      !customFields.some((cf) => cf.fieldId === selectedCustomField)
    ) {
      const newCustomField = {
        fieldId: selectedCustomField,
        fieldName: selectedField.name,
        value: isAuthenticatedValue,
        valueName: isAuthenticatedValue,
      };

      setCustomFields([...customFields, newCustomField]);

      const newOperations = [
        ...value.escalationRuleOperations,
        {
          type: EscalationRuleOperationType.ADD_CUSTOM_FIELD,
          enabled: true,
          key: selectedCustomField,
          value: isAuthenticatedValue,
        },
      ];

      onChange({ escalationRuleOperations: newOperations });
    }
  }, [
    selectedCustomField,
    ticketFields,
    customFields,
    value.escalationRuleOperations,
    onChange,
    setCustomFields,
  ]);

  const dropdownItems = useMemo(() => {
    const topicOption = {
      label: "✨ Topic",
      onClick: addQuackTopicTag,
    };

    const isAuthenticatedOption = {
      label: "🔐 Is Authenticated",
      onClick: addIsAuthenticatedField,
    };

    const enrichmentOptions = enrichments.map((enrichment) => ({
      label: enrichment.name,
      onClick: () => addEnrichmentField(enrichment.name),
    }));

    if (enrichmentOptions.length === 0) {
      return [topicOption, isAuthenticatedOption];
    }

    return [
      topicOption,
      isAuthenticatedOption,
      {
        label: "Enrichments",
        items: enrichmentOptions,
      },
    ];
  }, [addQuackTopicTag, addIsAuthenticatedField, addEnrichmentField, enrichments]);

  return (
    <>
      <Separator
        orientation="horizontal"
        size="4"
        style={{ marginTop: "12px" }}
      />
      <Flex direction={"column"} gap={"2"} mt="4">
        <Flex justify="between" align="center">
          <Text weight="bold" size="2">
            Custom Fields
          </Text>
          <IconButton color="gray" variant="ghost" size="1" onClick={onRemove}>
            <PlusIcon
              width={16}
              height={16}
              style={{ transform: "rotate(45deg)" }}
            />
          </IconButton>
        </Flex>
        <Text size="2" color="gray">
          Set custom field values for escalated tickets
        </Text>

        <Flex gap="2" align="center">
          <div style={{ flex: 1 }}>
            <Select
              options={customFieldOptions.filter(
                (option) =>
                  !customFields.some((cf) => cf.fieldId === option.value)
              )}
              value={selectedCustomField}
              onChange={(fieldId) => {
                setSelectedCustomField(fieldId);
                setSelectedCustomFieldValue("");
                setCustomFieldTextValue("");
              }}
              loading={loadingTicketFields}
              placeholder="Select a custom field..."
            />
          </div>
        </Flex>
        <Flex align="center" gap="2" mt="2">
          {<Separator orientation="horizontal" size="4" />}
        </Flex>
        <Flex align="center" gap="2">
          {selectedCustomField &&
            dropdownItems.length > 0 &&
            !customFieldValueOptions.length && (
              <DropdownMenu
                items={dropdownItems.map((option) => {
                  if ("items" in option) {
                    return {
                      label: option.label,
                      items: option.items.map((item) => ({
                        ...item,
                        icon: <PlusIcon size={14} />,
                      })),
                    };
                  }
                  return {
                    label: option.label,
                    onClick: option.onClick,
                    icon: <PlusIcon size={14} />,
                  };
                })}
              >
                <DropdownMenu.Trigger>
                  <Button
                    color="gray"
                    variant="surface"
                    highContrast
                    size="2"
                    mb="3"
                  >
                    <PlusIcon size={15} />
                    Add Dynamic Field
                  </Button>
                </DropdownMenu.Trigger>
              </DropdownMenu>
            )}
        </Flex>

        {selectedCustomField && (
          <>
            {customFieldValueOptions.length > 0 ? (
              <Select
                options={customFieldValueOptions}
                value={selectedCustomFieldValue}
                onChange={(valueId) => {
                  setSelectedCustomFieldValue(valueId);
                  setCustomFieldTextValue("");
                }}
                placeholder="Select a value..."
              />
            ) : (
              <Input
                placeholder="Enter custom field value..."
                value={customFieldTextValue}
                onChange={(e) => {
                  setCustomFieldTextValue(e.target.value);
                  setSelectedCustomFieldValue("");
                }}
              />
            )}

            <Button
              color="blue"
              variant="soft"
              size="1"
              onClick={addCustomField}
              disabled={
                !selectedCustomField ||
                (!selectedCustomFieldValue && !customFieldTextValue.trim())
              }
              style={{ marginTop: "8px" }}
            >
              Add Custom Field
            </Button>
          </>
        )}

        {/* Display Selected Custom Fields */}
        {customFields.length > 0 && (
          <Flex gap="1" wrap="wrap" mt="3">
            {customFields.map((customField) => (
              <Flex
                key={customField.fieldId}
                align="center"
                gap="1"
                style={{
                  backgroundColor: "var(--gray-3)",
                  padding: "4px 8px",
                  borderRadius: "4px",
                  border: "1px solid var(--gray-6)",
                }}
              >
                <Text size="1">
                  {customField.fieldName}: {customField.valueName}
                </Text>
                <IconButton
                  color="gray"
                  variant="ghost"
                  size="1"
                  onClick={() => removeCustomField(customField.fieldId)}
                  style={{ padding: "2px" }}
                >
                  <PlusIcon
                    width={12}
                    height={12}
                    style={{ transform: "rotate(45deg)" }}
                  />
                </IconButton>
              </Flex>
            ))}
          </Flex>
        )}
      </Flex>
    </>
  );
}
